import React from 'react';
import Select from 'react-select';
import rawTelemetryAnalyticsService from '../../services/rawTelemetryAnalyticsService';
import { LoadingSkeleton } from './LoadingSkeleton';

const PAGE_SIZE = 200;
export const MAX_TIMELINE_EVENTS = 5000;
const MAX_BACKGROUND_PAGE_BATCH = 5;
const DAY_MS = 24 * 60 * 60 * 1000;
const TIMELINE_REGION_DAYS = 7;
const ALL_DEVICES_OPTION_VALUE = '__all_devices__';

export function resolveAnalyticsScope(viewType, selectedTag, systemIds = []) {
  return { tag: viewType === 'device-tag' && systemIds.length === 0 ? selectedTag : null, systemIds };
}

export function NullValue() {
  return <span className="raw-null" title="Value not reported by source">NULL</span>;
}

function Value({ value, children }) {
  if (value === null || value === undefined || value === '') return <NullValue />;
  return children || String(value);
}

function formatNumber(value, digits = 1) {
  if (value === null || value === undefined) return null;
  return Number(value).toLocaleString(undefined, { maximumFractionDigits: digits });
}

function formatSeconds(value) {
  if (value === null || value === undefined) return null;
  if (value < 60) return `${value}s`;
  return `${Math.floor(value / 60)}m ${value % 60}s`;
}

const MODEM_UPTIME_CORRELATION_LABELS = {
  MATCHED: 'MATCHED',
  PENDING: 'PENDING',
  NOT_OBSERVED: 'NOT OBSERVED',
  UNAVAILABLE: 'UNAVAILABLE',
  NOT_APPLICABLE: 'NOT APPLICABLE',
};

export function modemUptimeCorrelationLabel(status) {
  return MODEM_UPTIME_CORRELATION_LABELS[status] || 'UNAVAILABLE';
}

export function modemUptimeCorrelationClass(status) {
  return `correlation-${String(status || 'UNAVAILABLE').toLowerCase().replace(/_/g, '-')}`;
}

export function formatEventTimestamp(value) {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const dateText = date.toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
  const timeText = date.toLocaleTimeString(undefined, {
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false, timeZoneName: 'short',
  });
  return `${dateText} · ${timeText}`;
}

function EventTimestamp({ value, className = '' }) {
  const formatted = formatEventTimestamp(value);
  if (!formatted) return <NullValue />;
  const date = value instanceof Date ? value : new Date(value);
  return <time className={`raw-event-timestamp ${className}`.trim()} dateTime={date.toISOString()}>{formatted}</time>;
}

const RECOVERY_TYPE_LABELS = {
  NO_ACTION: 'No action',
  MODEM_RLF: 'Modem RLF',
  PDN_TOGGLE: 'PDN toggle',
  LPM_TOGGLE: 'LPM toggle',
  MODEM_SSR: 'Modem SSR',
  DEVICE_REBOOT: 'Device reboot',
  FACTORY_RESET: 'Factory reset',
};

export function recoveryTypeLabel(value) {
  if (value === null || value === undefined || value === '') return null;
  const raw = String(value).trim();
  const normalized = raw.toUpperCase();
  const withoutPrefix = normalized
    .replace(/^DATA_STALL_RECOVERY_?/, '')
    .replace(/^RECOVERY_?/, '');
  if (RECOVERY_TYPE_LABELS[withoutPrefix]) return RECOVERY_TYPE_LABELS[withoutPrefix];
  return withoutPrefix
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function timelinePosition(timestamp, from, to) {
  const start = new Date(from).getTime();
  const end = new Date(to).getTime();
  const value = new Date(timestamp).getTime();
  if (![start, end, value].every(Number.isFinite) || end <= start) return 0;
  return Math.max(0.8, Math.min(99.2, ((value - start) / (end - start)) * 100));
}

export function buildTimelineLanes(items = []) {
  const grouped = new Map();
  [...items].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)).forEach((event) => {
    const key = event.systemId || 'unknown';
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key).push(event);
  });
  return [...grouped.entries()].map(([systemId, events]) => ({
    systemId,
    deviceTag: events.find((event) => event.deviceTag)?.deviceTag || null,
    events,
  }));
}

export function timelineEventKey(event = {}) {
  const identity = event.eventId ?? event.stallId ?? event.recoveryAttempts?.[0]?.recoveryId ?? '';
  return [event.eventType || 'DATA_STALL', event.systemId || '', identity, event.timestamp || ''].join(':');
}

export function appendEventPage(currentPage, nextPage) {
  const nextItems = nextPage?.items || [];
  if (!currentPage) return { ...nextPage, items: nextItems };
  const seen = new Set(currentPage.items?.map(timelineEventKey));
  const appendedItems = nextItems.filter((item) => {
    const key = timelineEventKey(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return { ...nextPage, items: [...(currentPage.items || []), ...appendedItems] };
}

export function capEventPage(eventPage, maxEvents = MAX_TIMELINE_EVENTS) {
  if (!eventPage) return eventPage;
  const items = eventPage.items || [];
  const totalElements = Number(eventPage.totalElements);
  const total = Number.isFinite(totalElements) ? totalElements : items.length;
  return {
    ...eventPage,
    items: items.slice(0, maxEvents),
    isCapped: total > maxEvents,
  };
}

export function layoutLaneEvents(events, from, to) {
  const lastPositionBySlot = [-Infinity, -Infinity, -Infinity];
  return events.map((event) => {
    const position = timelinePosition(event.timestamp, from, to);
    let slot = lastPositionBySlot.findIndex((lastPosition) => position - lastPosition >= 2.2);
    if (slot < 0) slot = lastPositionBySlot.indexOf(Math.min(...lastPositionBySlot));
    lastPositionBySlot[slot] = position;
    return { event, position, slot };
  });
}

export function timelineTicks(from, to) {
  const start = new Date(from).getTime();
  const end = new Date(to).getTime();
  if (![start, end].every(Number.isFinite) || end <= start) return [];
  const duration = end - start;
  const step = duration <= DAY_MS ? duration / 4 : DAY_MS;
  const ticks = [];
  for (let timestamp = start; timestamp < end; timestamp += step) {
    ticks.push({ ratio: (timestamp - start) / duration, value: new Date(timestamp) });
  }
  ticks.push({ ratio: 1, value: new Date(end) });
  return ticks;
}

export function timelineRegionCount(from, to) {
  const start = new Date(from).getTime();
  const end = new Date(to).getTime();
  if (![start, end].every(Number.isFinite) || end <= start) return 1;
  return Math.max(1, Math.ceil((end - start) / (TIMELINE_REGION_DAYS * DAY_MS)));
}

export function timelineTickLabel(value, from, to, isFirst = false) {
  const start = new Date(from).getTime();
  const end = new Date(to).getTime();
  const date = value instanceof Date ? value : new Date(value);
  const duration = end - start;
  if (!Number.isFinite(date.getTime()) || !Number.isFinite(duration)) return '';
  if (duration <= DAY_MS) {
    const time = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
    return isFirst ? `${date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} · ${time}` : time;
  }
  if (duration <= TIMELINE_REGION_DAYS * DAY_MS) {
    return date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  }
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function SummaryStats({ domain, summary }) {
  const items = domain === 'stall' ? [
    ['Stalls', summary?.totalStalls], ['Devices', summary?.affectedDeviceCount],
    ['Recovery rate', summary?.recoveryRatePercent == null ? null : `${formatNumber(summary.recoveryRatePercent, 1)}%`],
    ['Unrecovered', summary?.neverRecoveredStalls],
    ['Orphan recoveries', summary?.orphanedRecoveryCount],
  ] : [
    ['Total', summary?.eventCounts?.total ?? summary?.rawEventCount],
    ['UP', summary?.eventCounts?.up ?? summary?.recoveryEventCount],
    ['DOWN', summary?.eventCounts?.down ?? summary?.restartCount],
  ];
  return <div className="raw-timeline-stats">{items.map(([label, value]) => <div key={label}><strong>{value ?? '—'}</strong><span>{label}</span></div>)}</div>;
}

export function buildDeviceOptions(devices = [], domain) {
  return devices.filter((device) => device?.systemId).map((device) => {
    const eventCount = Number(device.eventCount ?? (domain === 'stall'
      ? device.stallCount
      : (Number(device.restartCount) || 0) + (Number(device.recoveryEventCount) || 0))) || 0;
    return { value: device.systemId, label: device.systemId, systemId: device.systemId, deviceTag: device.deviceTag || null, eventCount };
  }).sort((left, right) => right.eventCount - left.eventCount
    || left.label.localeCompare(right.label) || left.systemId.localeCompare(right.systemId));
}

function DeviceMultiSelect({ devices, domain, selectedIds, onChange }) {
  const deviceOptions = buildDeviceOptions(devices, domain);
  const allOption = { value: ALL_DEVICES_OPTION_VALUE, label: 'All devices', deviceTag: null, isAll: true };
  const options = [allOption, ...deviceOptions];
  const selected = deviceOptions.filter((option) => selectedIds.includes(option.value));
  const placeholder = selectedIds.length > 0 ? `${selectedIds.length} of ${devices.length} devices` : `All devices (${devices.length})`;
  return <div className="raw-device-picker"><Select
    classNamePrefix="raw-device-select" isMulti isClearable closeMenuOnSelect={false} hideSelectedOptions={false}
    controlShouldRenderValue={false} options={options} value={selected}
    onChange={(values) => {
      const next = values || [];
      onChange(next.some((value) => value.isAll) ? [] : next.map((value) => value.value));
    }}
    placeholder={placeholder} noOptionsMessage={() => 'No matching devices'}
    formatOptionLabel={(option, context) => <div className="raw-device-option">
      <div><strong>{option.label}</strong>{option.deviceTag && <small>{option.deviceTag}</small>}</div><small>{option.isAll ? `${devices.length} available` : `${option.eventCount} event${option.eventCount === 1 ? '' : 's'}`}</small>
      {context.context === 'menu' && (option.isAll ? selectedIds.length === 0 : selectedIds.includes(option.value)) && <b>✓</b>}
    </div>}
  /></div>;
}

export function TimelineMarker({ domain, event, position, slot, onHover, onLeave, onSelect }) {
  const isStall = domain === 'stall';
  const attemptCount = event.recoveryAttempts?.length || 0;
  const isOrphanRecovery = isStall && event.eventType === 'ORPHAN_RECOVERY';
  const isNoRecoveryObserved = isStall && !isOrphanRecovery
    && (event.recoveryOutcome === 'NO_RECOVERY_OBSERVED'
      || (event.recoveryOutcome == null && event.recoveryState === 'NEVER_RECOVERED'
        && attemptCount === 0 && event.observedDurationSeconds == null));
  const isSubsystemUp = !isStall && event.eventType === 'SUBSYSTEM_UP';
  const correlationStatus = isSubsystemUp ? (event.modemUptimeCorrelation?.status || 'UNAVAILABLE') : null;
  const correlationClass = isSubsystemUp ? modemUptimeCorrelationClass(correlationStatus) : '';
  const markerClass = isStall
    ? `raw-timeline-marker stall ${isOrphanRecovery ? 'orphan-recovery' : event.recoveryState === 'RECOVERED' ? 'recovered' : 'unrecovered'}`
    : `raw-timeline-marker ssr ${event.eventType === 'SUBSYSTEM_DOWN' ? 'down' : 'up'}${correlationClass ? ` ${correlationClass}` : ''}`;
  const markerLabel = isOrphanRecovery ? 'Recovery without stall anchor'
    : isNoRecoveryObserved ? 'Data stall; no recovery observed'
      : isStall ? `Data stall; ${event.recoveryState === 'RECOVERED' ? 'recovered' : 'recovery attempts not successful'}`
        : `${event.eventType}${isSubsystemUp ? `; modem uptime ${modemUptimeCorrelationLabel(correlationStatus).toLowerCase()}` : ''}`;
  return <button type="button" className={markerClass}
    style={{ left: `${position}%`, top: `${18 + (slot * 16)}px` }}
    onMouseEnter={(mouseEvent) => onHover(event, mouseEvent.currentTarget)}
    onMouseLeave={onLeave}
    onFocus={(focusEvent) => onHover(event, focusEvent.currentTarget)}
    onBlur={onLeave}
    onClick={() => onSelect && onSelect(event)}
    aria-label={`${markerLabel} on ${event.systemId} at ${formatEventTimestamp(event.timestamp) || 'unknown time'}`}>
  </button>;
}

export function EventTimeline({ domain, summary, eventPage, activeSystemId, onHover, onLeave, onSelect }) {
  const items = eventPage?.items || [];
  const lanes = buildTimelineLanes(items);
  const from = eventPage?.from || summary?.from;
  const to = eventPage?.to || summary?.to;
  const ticks = timelineTicks(from, to);
  const regions = timelineRegionCount(from, to);
  if (items.length === 0) return <div className="raw-timeline-empty"><strong>No events found</strong><span>No events match the current time range and device selection.</span></div>;
  return <div className="raw-timeline-scroll" tabIndex="0" aria-label={`${domain === 'stall' ? 'Data stall' : 'Modem subsystem restart'} timeline`}><div className="raw-timeline-canvas" style={{ width: `${regions * 100}%` }}>
    <div className="raw-timeline-axis-label" />
    <div className="raw-timeline-axis">{ticks.map((tick, index) => <span key={tick.ratio} style={{ left: `${tick.ratio * 100}%` }} title={formatEventTimestamp(tick.value) || ''}>{timelineTickLabel(tick.value, from, to, index === 0)}</span>)}</div>
    {lanes.map((lane) => <React.Fragment key={lane.systemId}>
      <div className={`raw-lane-label${lane.systemId === activeSystemId ? ' active' : ''}`}><div><strong>{lane.systemId}</strong><small>{lane.deviceTag || `${lane.events.length} events`}</small></div>{lane.deviceTag && <em>{lane.events.length}</em>}</div>
      <div className={`raw-lane-track${lane.systemId === activeSystemId ? ' active' : ''}`}><div className="raw-lane-line" />
        {ticks.map((tick) => <i className="raw-lane-grid" key={tick.ratio} style={{ left: `${tick.ratio * 100}%` }} />)}
        {layoutLaneEvents(lane.events, from, to).map(({ event, position, slot }, index) => <TimelineMarker key={`${event.eventId || event.stallId}-${event.timestamp}-${index}`} domain={domain} event={event} position={position} slot={slot} onHover={onHover} onLeave={onLeave} onSelect={onSelect} />)}
      </div>
    </React.Fragment>)}
  </div></div>;
}

function HoverField({ label, value, children }) {
  return <div className="raw-hover-field"><span>{label}</span><strong><Value value={value}>{children}</Value></strong></div>;
}

export function stallDurationPresentation(event) {
  if (event?.eventType === 'ORPHAN_RECOVERY') {
    return { value: 'N/A', qualifier: 'No stall anchor' };
  }
  if (event?.recoveryState === 'RECOVERED' && event.durationSeconds !== null && event.durationSeconds !== undefined) {
    return { value: formatSeconds(event.durationSeconds), qualifier: 'Recovered · exact' };
  }
  if (event?.observedDurationSeconds !== null && event?.observedDurationSeconds !== undefined) {
    return { value: `≥${formatSeconds(event.observedDurationSeconds)}`, qualifier: 'Not recovered · lower bound' };
  }
  return { value: 'Unknown', qualifier: 'Not recovered · no recovery observed' };
}

function StallDurationStat({ event }) {
  const duration = stallDurationPresentation(event);
  return <div className="raw-hover-stat"><span>Duration</span><strong>{duration.value}</strong><small>{duration.qualifier}</small></div>;
}

function ModemUptimeCorrelationDetails({ correlation }) {
  const status = correlation?.status || 'UNAVAILABLE';
  const statusLabel = modemUptimeCorrelationLabel(status);
  const statusClass = modemUptimeCorrelationClass(status);
  const statusDescription = {
    MATCHED: 'A modem restart was inferred within the 60-second watch.',
    PENDING: 'Waiting for registration data until the 60-second watch ends.',
    NOT_OBSERVED: 'No matching modem uptime observation arrived in 60 seconds.',
    UNAVAILABLE: 'Registration data did not include usable modem uptime.',
  }[status] || 'Correlation is not applicable to this event.';
  return <div className={`raw-uptime-correlation ${statusClass}`}>
    <div className="raw-uptime-correlation-header"><div><span>Modem uptime correlation</span><strong>{statusLabel}</strong></div><small>60-second watch</small></div>
    <p>{statusDescription}</p>
    {status === 'MATCHED' && <div className="raw-uptime-correlation-fields">
      <HoverField label="Modem uptime" value={correlation.modemUptimeSeconds}>{formatSeconds(correlation.modemUptimeSeconds)}</HoverField>
      <HoverField label="Delta" value={correlation.deltaSeconds}>{formatSeconds(correlation.deltaSeconds)}</HoverField>
      <HoverField label="Registration" value={correlation.registrationTimestamp}><EventTimestamp value={correlation.registrationTimestamp} /></HoverField>
      <HoverField label="Inferred restart" value={correlation.inferredRestartTimestamp}><EventTimestamp value={correlation.inferredRestartTimestamp} /></HoverField>
    </div>}
    {status === 'PENDING' && <div className="raw-uptime-correlation-watch"><span>Watch ends</span><strong><EventTimestamp value={correlation.watchEndsAt} /></strong></div>}
  </div>;
}

export function RecoveryChain({ event }) {
  const attempts = event?.recoveryAttempts || [];
  const isOrphanRecovery = event?.eventType === 'ORPHAN_RECOVERY';
  return <div className="raw-hover-chain"><div className="raw-hover-chain-heading"><b>Recovery chain</b><span>{attempts.length} step{attempts.length === 1 ? '' : 's'}</span></div>
    <div className="raw-hover-chain-scroll"><div className={`raw-hover-chain-track${isOrphanRecovery ? ' orphan' : ''}`}>{isOrphanRecovery ? <div className="raw-hover-chain-step orphan-step"><i>?</i><div><b>Stall anchor not found</b><small>Six-hour lookback contained no anchor</small><em>ORPHAN RECOVERY</em></div></div> : <div className="raw-hover-chain-step stall-step"><i>●</i><div><b>Stall detected</b><small><EventTimestamp value={event.timestamp} /></small><em>Start</em></div></div>}
      {attempts.map((attempt) => <div className="raw-hover-chain-step" key={`${attempt.recoveryId}-${attempt.attemptSequence}`}><i>{attempt.attemptSequence}</i><div><b title={attempt.action || undefined}>{recoveryTypeLabel(attempt.action) || <Value value={attempt.action} />}</b><small><EventTimestamp value={attempt.timestamp} /> · +{formatSeconds(attempt.elapsedSeconds) || 'NULL'}</small><em><Value value={attempt.status} /></em><small>recovery #{attempt.recoveryId}</small></div></div>)}
    </div></div>
    {!isOrphanRecovery && attempts.length === 0 && <div className="raw-hover-chain-none">No recovery attempts reported.</div>}
  </div>;
}

export function EventHoverCard({ domain, event, style }) {
  if (!event) return null;
  const isStall = domain === 'stall';
  const isOrphanRecovery = isStall && event.eventType === 'ORPHAN_RECOVERY';
  const isNoRecoveryObserved = isStall && !isOrphanRecovery
    && (event.recoveryOutcome === 'NO_RECOVERY_OBSERVED'
      || (event.recoveryOutcome == null && event.recoveryState === 'NEVER_RECOVERED'
        && (event.recoveryAttempts || []).length === 0 && event.observedDurationSeconds == null));
  const stallState = isOrphanRecovery ? 'orphan'
    : isNoRecoveryObserved ? 'no-recovery' : event.recoveryState === 'RECOVERED' ? 'recovered' : 'not-recovered';
  const ssrState = event.eventType === 'SUBSYSTEM_DOWN' ? 'down' : 'up';
  const stallStateLabel = isOrphanRecovery ? 'RECOVERY WITHOUT STALL ANCHOR'
    : isNoRecoveryObserved ? 'NO RECOVERY OBSERVED'
      : stallState === 'recovered' ? 'RECOVERED' : 'NOT RECOVERED';
  return <div className="raw-event-hover" style={style} role="tooltip">
    <div className="raw-hover-header"><div className="raw-hover-heading"><span>{isOrphanRecovery ? 'ORPHAN RECOVERY' : isStall ? 'DATA STALL' : 'SUBSYSTEM EVENT'}</span><b>{event.systemId}</b><small><span className="raw-time-label">Event time</span><EventTimestamp value={event.timestamp} /></small></div><strong className={`raw-event-state ${isStall ? `stall-${stallState}` : `ssr-${ssrState}`}`}>{isStall ? stallStateLabel : (ssrState === 'down' ? 'SUBSYSTEM DOWN' : 'SUBSYSTEM UP')}</strong></div>
    {isStall ? <div className="raw-hover-summary"><StallDurationStat event={event} /><div className="raw-hover-stat"><span>Recovery steps</span><strong>{event.recoveryAttempts?.length || 0}</strong><small>{isOrphanRecovery ? 'anchor missing' : event.recoveryAttempts?.length ? 'associated' : 'none observed'}</small></div><div className="raw-hover-stat"><span>Stall ID</span><strong>{event.telemetryStallId == null ? <NullValue /> : `#${event.telemetryStallId}`}</strong><small>{event.recoveryOutcome || event.durationType || 'UNKNOWN'}</small></div></div>
      : <div className="raw-hover-summary"><div className="raw-hover-stat"><span>Event ID</span><strong>#{event.eventId}</strong><small>Raw event</small></div><div className="raw-hover-stat"><span>Event type</span><strong><Value value={event.eventType} /></strong><small>Independent event</small></div><div className="raw-hover-stat"><span>Subsystem</span><strong><Value value={event.subsystem} /></strong><small>Reported field</small></div></div>}
    <div className="raw-hover-fields">
      <HoverField label="System ID" value={event.systemId} />
      <HoverField label="Device tag" value={event.deviceTag} />
      {isStall ? <><HoverField label="Subsystem" value={event.subsystem} /><HoverField label="Direction" value={event.direction} /><HoverField label="Reason" value={event.reason} /></>
        : <HoverField label="Reason" value={event.reason} />}
    </div>
    {isStall && <RecoveryChain event={event} />}
    {!isStall && event.eventType === 'SUBSYSTEM_UP' && <ModemUptimeCorrelationDetails correlation={event.modemUptimeCorrelation} />}
  </div>;
}

function TimelineSection({ domain, timeRange, viewType, selectedTag, onEventClick }) {
  const isStall = domain === 'stall';
  const [summary, setSummary] = React.useState(null);
  const [eventPage, setEventPage] = React.useState(null);
  const [availableDevices, setAvailableDevices] = React.useState([]);
  const [selectedIds, setSelectedIds] = React.useState([]);
  const [hoveredEvent, setHoveredEvent] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [loadMoreError, setLoadMoreError] = React.useState(null);
  const [isCapped, setIsCapped] = React.useState(false);
  const [retry, setRetry] = React.useState(0);
  const scopeKey = `${viewType}:${selectedTag || ''}:${typeof timeRange === 'string' ? timeRange : JSON.stringify(timeRange)}`;
  const [activeScopeKey, setActiveScopeKey] = React.useState(scopeKey);

  React.useEffect(() => {
    setActiveScopeKey(scopeKey);
    setSelectedIds((current) => current.length === 0 ? current : []); setHoveredEvent(null); setEventPage(null); setSummary(null);
    setAvailableDevices([]); setError(null);
    setLoadMoreError(null); setIsCapped(false); setLoadingMore(false);
  }, [scopeKey]);
  React.useEffect(() => {
    if (activeScopeKey !== scopeKey || (viewType === 'device-tag' && !selectedTag)) return undefined;
    let active = true;
    let initialPageLoaded = false;
    setLoading(true); setLoadingMore(false); setError(null); setLoadMoreError(null); setHoveredEvent(null); setIsCapped(false);
    const common = { timeRange, ...resolveAnalyticsScope(viewType, selectedTag, selectedIds), filters: {} };
    const getEvents = (page) => (isStall
      ? rawTelemetryAnalyticsService.getDataStallEvents({ ...common, page, pageSize: PAGE_SIZE })
      : rawTelemetryAnalyticsService.getSubsystemRestartEvents({ ...common, page, pageSize: PAGE_SIZE }));
    const summaryRequest = isStall ? rawTelemetryAnalyticsService.getDataStallSummary(common) : rawTelemetryAnalyticsService.getSubsystemRestartSummary(common);

    const updateDevices = (summaryData, eventsData) => {
      if (selectedIds.length !== 0) return;
      const devices = new Map((summaryData.affectedDevices || []).map((device) => [device.systemId, device]));
      (eventsData.items || []).forEach((event) => {
        if (event.systemId && !devices.has(event.systemId)) {
          devices.set(event.systemId, { systemId: event.systemId, deviceTag: event.deviceTag || null, eventCount: 1 });
        }
      });
      setAvailableDevices([...devices.values()]);
    };

    const loadTimeline = async () => {
      try {
        const [summaryData, firstPage] = await Promise.all([summaryRequest, getEvents(1)]);
        if (!active) return;
        initialPageLoaded = true;
        const firstEvents = capEventPage(firstPage);
        setSummary(summaryData);
        setEventPage(firstEvents);
        setIsCapped(Boolean(firstEvents.isCapped));
        updateDevices(summaryData, firstEvents);
        setLoading(false);

        const maxPages = Math.ceil(MAX_TIMELINE_EVENTS / PAGE_SIZE);
        const pagesToFetch = Math.min(firstPage.totalPages || 1, maxPages);
        const remainingPageNumbers = Array.from({ length: Math.max(0, pagesToFetch - 1) }, (_, index) => index + 2);
        if (remainingPageNumbers.length === 0) return;

        setLoadingMore(true);
        let mergedEvents = firstPage;
        for (let index = 0; index < remainingPageNumbers.length; index += MAX_BACKGROUND_PAGE_BATCH) {
          const pageBatch = remainingPageNumbers.slice(index, index + MAX_BACKGROUND_PAGE_BATCH);
          const remainingPages = await Promise.all(pageBatch.map(getEvents));
          if (!active) return;
          mergedEvents = capEventPage(remainingPages.reduce(appendEventPage, mergedEvents));
          setEventPage(mergedEvents);
          setIsCapped(Boolean(mergedEvents.isCapped || (firstPage.totalPages || 1) > maxPages));
          updateDevices(summaryData, mergedEvents);
        }
      } catch (requestError) {
        if (!active) return;
        if (initialPageLoaded) {
          setLoadMoreError({ message: requestError.message, sourceStatus: requestError.sourceStatus || 'ERROR' });
        } else {
          setError({ message: requestError.message, sourceStatus: requestError.sourceStatus || 'ERROR' });
        }
      } finally {
        if (active) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    };

    loadTimeline();
    return () => { active = false; };
  }, [activeScopeKey, domain, timeRange, viewType, selectedTag, selectedIds, retry, isStall, scopeKey]);

  const resetEvents = (ids = selectedIds) => {
    setSelectedIds(ids); setHoveredEvent(null); setEventPage(null); setSummary(null); setError(null);
  };

  const title = isStall ? 'Data stall timeline' : 'Modem subsystem restart timeline';
  const description = isStall ? 'Markers show stalls and recovery observations. Hover a marker to inspect its state and recovery chain.' : 'DOWN and UP events are independent; hover a marker to inspect reported fields.';
  const showHover = (event, anchor) => {
    const rect = anchor.getBoundingClientRect();
    const width = isStall ? 572 : 460;
    const estimatedHeight = isStall ? 460 : (event?.eventType === 'SUBSYSTEM_UP' ? 400 : 270);
    const left = Math.max(10, Math.min(window.innerWidth - width - 10, rect.left + (rect.width / 2) - (width / 2)));
    const top = rect.bottom + estimatedHeight + 10 < window.innerHeight ? rect.bottom + 8 : Math.max(10, rect.top - estimatedHeight - 8);
    setHoveredEvent({ event, style: { left, top, width } });
  };
  const selectEvent = (event) => {
    if (event?.systemId && onEventClick) onEventClick(event);
  };
  return <section className="raw-timeline-section"><div className="raw-timeline-header">
    <div className="raw-timeline-title"><div><h3>{title}</h3><p>{description}</p></div></div>
    <SummaryStats domain={domain} summary={summary} />
    <div className="raw-timeline-tools"><DeviceMultiSelect devices={availableDevices} domain={domain} selectedIds={selectedIds} onChange={resetEvents} /></div>
  </div>
  {loading && !eventPage ? <div className="raw-timeline-loading"><LoadingSkeleton height="210px" /></div> : error ? <div className="raw-timeline-state"><strong>{error.sourceStatus === 'TIMEOUT' ? 'Timeline request timed out' : 'Timeline unavailable'}</strong><span>{error.message}</span><button type="button" onClick={() => { resetEvents(); setRetry((value) => value + 1); }}>Retry</button></div> : <>
    {loadingMore && <div className="raw-timeline-progress" role="status">Loading additional events…</div>}
    {loadMoreError && <div className="raw-timeline-progress" role="status">Some additional events could not be loaded: {loadMoreError.message}</div>}
    {isCapped && <div className="raw-timeline-progress" role="status">Showing the first {MAX_TIMELINE_EVENTS.toLocaleString()} events. Narrow the time range or select devices for a smaller detailed view.</div>}
    <EventTimeline domain={domain} summary={summary} eventPage={eventPage} activeSystemId={hoveredEvent?.event?.systemId} onHover={showHover} onLeave={() => setHoveredEvent(null)} onSelect={selectEvent} />
  </>}
  <EventHoverCard domain={domain} event={hoveredEvent?.event} style={hoveredEvent?.style} /></section>;
}

export default function RawAnalyticsPanels({ domain, onEventClick, ...props }) {
  const domains = domain ? [domain] : ['stall', 'ssr'];
  return <div className="raw-timeline-stack">{domains.map((timelineDomain) => <TimelineSection key={timelineDomain} domain={timelineDomain} onEventClick={onEventClick} {...props} />)}</div>;
}
